import React, { useCallback, useState } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  MenuList,
  Text,
  useStyleConfig,
  VStack,
  Heading,
  useOutsideClick,
} from '@chakra-ui/react';
import { ChakraSelectButton } from './ChakraSelectButton';

export interface ChakraSelectParentsCategory {
  label?: string;
  selectable: boolean;
  data: Array<ChakraSelectParentData>;
}

export interface ChakraSelectChildrenCategory {
  label?: string;
  selectable: boolean;
  data: Array<ChakraSelectChildData>;
}

export interface ChakraSelectSubChildrenCategory {
  label?: string;
  selectable: boolean;
  data: Array<ChakraSelectSubChildData>;
}

export interface ChakraSelectParentData {
  id: string;
  name: string;
}

export interface ChakraSelectChildData {
  id: string;
  name: string;
  parent: string;
}

export interface ChakraSelectSubChildData {
  id: string;
  name: string;
  child: string;
}

export interface ChakraSelectSelected {
  id: string;
  type: CHAKRA_SELECT_TYPE;
  name: string;
}
export interface ChakraSelectProps {
  variant?: string;
  parents?: ChakraSelectParentsCategory;
  children?: ChakraSelectChildrenCategory;
  subChildren?: ChakraSelectSubChildrenCategory;
  onChange: (type: CHAKRA_SELECT_TYPE, value: any) => void;
  selected?: ChakraSelectSelected;
  headline?: string;
  emptyStateText: string;
  trailingIcon?: any;
  closeOnSelect?: boolean;
}

export enum CHAKRA_SELECT_TYPE {
  PARENT = 'PARENT',
  CHILD = 'CHILD',
  SUB_CHILD = 'SUB_CHILD',
}
export const ChakraSelect = (props: ChakraSelectProps): JSX.Element => {
  const {
    variant,
    parents,
    children,
    subChildren,
    onChange,
    selected,
    headline,
    emptyStateText,
    trailingIcon,
    closeOnSelect,
  } = props;
  const boxStyles = useStyleConfig('Box', {
    variant: variant ? variant : 'chakraSelect.Box',
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const ref = React.useRef();

  useOutsideClick({
    // @ts-ignore
    ref: ref,
    handler: () => setIsOpen(false),
  });
  const handleClick = useCallback(
    (
      type: CHAKRA_SELECT_TYPE,
      item: ChakraSelectParentData | ChakraSelectChildData | ChakraSelectSubChildData,
    ) => {
      onChange(type, item.id);
      if (closeOnSelect) {
        setIsOpen(false);
      }
    },
    [onChange, closeOnSelect],
  );

  return (
    <Menu matchWidth={true} closeOnSelect={closeOnSelect} isOpen={isOpen} closeOnBlur={true}>
      <Box
        __css={boxStyles}
        onClick={() => setIsOpen(!isOpen)}
        // @ts-ignore
        ref={ref}>
        <ChakraSelectButton
          trailingIcon={trailingIcon}
          headline={headline}
          information={selected?.name ? selected.name : emptyStateText}
        />
        <MenuList boxShadow={'base'}>
          <VStack overflowY={'auto'} maxH={64} alignItems={'stretch'}>
            {parents &&
              parents.data.map((item, idx) => {
                return (
                  <MenuItem
                    _focus={{ bg: 'none' }}
                    key={idx}
                    as={'div'}
                    p={0}
                    flexDirection={'column'}
                    alignItems={'stretch'}>
                    <Text
                      fontSize={'xs'}
                      onClick={() => handleClick(CHAKRA_SELECT_TYPE.PARENT, item)}
                      bg={
                        selected?.type === CHAKRA_SELECT_TYPE.PARENT && selected?.id === item.id
                          ? 'blue.500'
                          : 'none'
                      }
                      color={'gray.600'}
                      textTransform={'uppercase'}
                      borderWidth={1}
                      borderTop={'none'}
                      borderBottomColor={'gray.500'}
                      borderRight={'none'}
                      borderLeft={'none'}
                      ml={4}
                      mr={4}
                      mb={2}
                      pt={2}
                      pb={2}
                      fontWeight={'bold'}>
                      {item.name}
                    </Text>
                    {children?.label &&
                      children.data.filter(child => child.parent === item.id).length > 0 && (
                        <Heading
                          as={'h4'}
                          size={'xs'}
                          _focus={{ bg: 'none' }}
                          pl={6}
                          pt={2}
                          pb={2}
                          color={'gray.600'}
                          bg={'none'}>
                          {children.label}:
                        </Heading>
                      )}
                    {children &&
                      children.data
                        .filter(child => child.parent === item.id)
                        .map((child, idx) => {
                          return (
                            <MenuItem
                              key={idx}
                              bg={'none'}
                              as={'div'}
                              p={0}
                              _hover={{ bg: 'none' }}
                              _selected={{ bg: 'none' }}
                              _active={{ bg: 'none' }}
                              _focus={{ bg: 'none' }}
                              flexDirection={'column'}
                              alignItems={'stretch'}>
                              <Text
                                onClick={() => handleClick(CHAKRA_SELECT_TYPE.CHILD, child)}
                                bg={
                                  selected?.type === CHAKRA_SELECT_TYPE.CHILD &&
                                  selected?.id === child.id
                                    ? 'blue.500'
                                    : 'none'
                                }
                                color={
                                  selected?.type === CHAKRA_SELECT_TYPE.CHILD &&
                                  selected?.id === child.id
                                    ? 'white'
                                    : 'none'
                                }
                                pl={6}
                                pt={2}
                                pb={2}
                                _hover={{
                                  backgroundColor: 'blue.100',
                                  cursor: 'pointer',
                                }}>
                                {child.name}
                              </Text>
                              {subChildren?.label &&
                                subChildren &&
                                subChildren.data.filter(subChild => subChild.child === child.id)
                                  .length > 0 && (
                                  <Heading
                                    as={'h4'}
                                    size={'xs'}
                                    _focus={{ bg: 'none' }}
                                    pl={10}
                                    pt={2}
                                    pb={2}
                                    color={'gray.600'}
                                    bg={'none'}>
                                    {subChildren.label}:
                                  </Heading>
                                )}
                              {subChildren &&
                                subChildren.data
                                  .filter(subChild => subChild.child === child.id)
                                  .map((subChild, idx) => {
                                    return (
                                      <MenuItem
                                        key={idx}
                                        bg={'none'}
                                        as={'div'}
                                        p={0}
                                        _hover={{ bg: 'none' }}
                                        _selected={{ bg: 'none' }}
                                        _active={{ bg: 'none' }}
                                        _focus={{ bg: 'none' }}
                                        flexDirection={'column'}
                                        alignItems={'stretch'}>
                                        <Text
                                          onClick={() =>
                                            handleClick(CHAKRA_SELECT_TYPE.SUB_CHILD, subChild)
                                          }
                                          bg={
                                            selected?.type === CHAKRA_SELECT_TYPE.SUB_CHILD &&
                                            selected?.id === subChild.id
                                              ? 'blue.500'
                                              : 'none'
                                          }
                                          color={
                                            selected?.type === CHAKRA_SELECT_TYPE.SUB_CHILD &&
                                            selected?.id === subChild.id
                                              ? 'white'
                                              : 'none'
                                          }
                                          pl={10}
                                          pt={2}
                                          pb={2}
                                          _hover={{
                                            backgroundColor: 'blue.100',
                                            cursor: 'pointer',
                                          }}>
                                          {subChild.name}
                                        </Text>
                                      </MenuItem>
                                    );
                                  })}
                            </MenuItem>
                          );
                        })}
                  </MenuItem>
                );
              })}
          </VStack>
        </MenuList>
      </Box>
    </Menu>
  );
};
